/**
 * @element qds-hero
 *
 * A flexible hero banner with eyebrow, title, body copy, optional media, and primary/secondary calls to action.
 *
 * @attr {string} eyebrow - Small label shown above the main title
 * @attr {string} heading - Primary hero headline (named `heading` rather than `title` to avoid colliding with the global HTML `title` tooltip attribute)
 * @attr {string} body - Supporting paragraph copy
 * @attr {string} media-src - Optional image URL
 * @attr {string} media-alt - Alt text for the hero image
 * @attr {string} primary-label - Label for the primary action
 * @attr {string} primary-href - Href for the primary action
 * @attr {string} primary-target - Target for the primary action
 * @attr {string} secondary-label - Label for the secondary action
 * @attr {string} secondary-href - Href for the secondary action
 * @attr {string} secondary-target - Target for the secondary action
 * @attr {string} align - Content alignment: left, center, or right
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import { applyTypographyStyle } from '../shared/typography-utils.js';
import { sanitizeUrl } from '../shared/url-utils.js';

loadBaseCSS();

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export class QdsHero extends HTMLElement {
  private _eyebrow = '';
  private _heading = '';
  private _body = '';
  private _mediaSrc = '';
  private _mediaAlt = '';
  private _primaryLabel = '';
  private _primaryHref = '';
  private _primaryTarget = '_self';
  private _secondaryLabel = '';
  private _secondaryHref = '';
  private _secondaryTarget = '_self';
  private _align: 'left' | 'center' | 'right' = 'left';
  private _typography: Record<string, unknown> = {};

  static get observedAttributes(): string[] {
    return [
      'eyebrow',
      'heading',
      'body',
      'media-src',
      'media-alt',
      'primary-label',
      'primary-href',
      'primary-target',
      'secondary-label',
      'secondary-href',
      'secondary-target',
      'align'
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.render();
  }

  set typography(value: Record<string, unknown> | undefined) {
    this._typography = value ? { ...value } : {};
    this.render();
  }

  get typography(): Record<string, unknown> {
    return this._typography;
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    switch (name) {
      case 'eyebrow':
        this._eyebrow = newValue || '';
        break;
      case 'heading':
        this._heading = newValue || '';
        break;
      case 'body':
        this._body = newValue || '';
        break;
      case 'media-src':
        this._mediaSrc = newValue || '';
        break;
      case 'media-alt':
        this._mediaAlt = newValue || '';
        break;
      case 'primary-label':
        this._primaryLabel = newValue || '';
        break;
      case 'primary-href':
        this._primaryHref = newValue || '';
        break;
      case 'primary-target':
        this._primaryTarget = newValue || '_self';
        break;
      case 'secondary-label':
        this._secondaryLabel = newValue || '';
        break;
      case 'secondary-href':
        this._secondaryHref = newValue || '';
        break;
      case 'secondary-target':
        this._secondaryTarget = newValue || '_self';
        break;
      case 'align':
        this._align = newValue === 'center' || newValue === 'right' ? newValue : 'left';
        break;
      default:
        break;
    }

    this.render();
  }

  private _renderAction(label: string, href: string, target: string, variant: 'primary' | 'secondary'): string {
    if (!label) {
      return '';
    }

    const safeHref = escapeHtml(sanitizeUrl(href) || '#');
    const safeTarget = escapeHtml(target || '_self');
    const rel = safeTarget === '_blank' ? ' rel="noopener noreferrer"' : '';

    return `
      <a
        class="hero__action hero__action--${variant}"
        href="${safeHref}"
        target="${safeTarget}"${rel}
      >
        ${escapeHtml(label)}
      </a>
    `;
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const hasMedia = Boolean(this._mediaSrc.trim());

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .hero {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
          gap: 24px;
          align-items: stretch;
          padding: 24px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 12px;
          background:
            radial-gradient(circle at top left, rgba(66, 133, 244, 0.14), transparent 36%),
            linear-gradient(135deg, rgba(12, 76, 135, 0.06), rgba(66, 133, 244, 0.02) 48%, rgba(255, 255, 255, 0.92));
          box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
        }

        .hero--no-media {
          grid-template-columns: minmax(0, 1fr);
        }

        .hero__content {
          display: grid;
          align-content: center;
          gap: 16px;
          min-width: 0;
          text-align: ${this._align};
          justify-items: ${this._align === 'center' ? 'center' : this._align === 'right' ? 'end' : 'start'};
        }

        .hero__eyebrow {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .hero__title {
          margin: 0;
          font-size: clamp(2rem, 2vw + 1.4rem, 3.6rem);
          line-height: 0.98;
          font-weight: 600;
          max-width: none;
        }

        .hero__body {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: clamp(1rem, 0.4vw + 0.95rem, 1.125rem);
          line-height: 1.6;
          max-width: 60ch;
        }

        .hero__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .hero__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          padding: 0 18px;
          border: 1px solid transparent;
          border-radius: 8px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }

        .hero__action--primary {
          background: var(--input-focus-border, #4285f4);
          color: #ffffff;
        }

        .hero__action--primary:hover {
          background: #2f74e9;
        }

        .hero__action--secondary {
          border-color: var(--theme-border-color, #e0e0e0);
          background: rgba(255, 255, 255, 0.86);
          color: var(--theme-text-primary, #333333);
        }

        .hero__action--secondary:hover {
          background: var(--theme-background-secondary, #f8f8f8);
        }

        .hero__media {
          position: relative;
          min-height: 260px;
          border: 1px solid rgba(12, 76, 135, 0.1);
          border-radius: 12px;
          overflow: hidden;
          background:
            linear-gradient(135deg, rgba(12, 76, 135, 0.12), rgba(66, 133, 244, 0.18)),
            linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(248, 248, 248, 0.9));
        }

        .hero__media img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        @media (max-width: 900px) {
          .hero,
          .hero--no-media {
            grid-template-columns: minmax(0, 1fr);
          }

          .hero__content {
            justify-items: start;
            text-align: left;
          }

          .hero__title {
            max-width: none;
          }
        }
      </style>
      <section class="hero ${hasMedia ? '' : 'hero--no-media'}" part="hero">
        <div class="hero__content" part="content">
          ${this._eyebrow ? `<p class="hero__eyebrow" part="eyebrow">${escapeHtml(this._eyebrow)}</p>` : ''}
          <h1 class="hero__title" part="title">${escapeHtml(this._heading || 'Hero title')}</h1>
          ${this._body ? `<p class="hero__body" part="body">${escapeHtml(this._body)}</p>` : ''}
          ${(this._primaryLabel || this._secondaryLabel) ? `
            <div class="hero__actions" part="actions">
              ${this._renderAction(this._primaryLabel, this._primaryHref, this._primaryTarget, 'primary')}
              ${this._renderAction(this._secondaryLabel, this._secondaryHref, this._secondaryTarget, 'secondary')}
            </div>
          ` : ''}
        </div>
        ${hasMedia ? `
          <div class="hero__media" part="media">
            <img src="${escapeHtml(this._mediaSrc)}" alt="${escapeHtml(this._mediaAlt || this._heading || 'Hero media')}">
          </div>
        ` : ''}
      </section>
    `;

    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.hero__eyebrow'), this._typography.eyebrow);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.hero__title'), this._typography.title);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.hero__body'), this._typography.body);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.hero__action--primary'), this._typography.primaryAction);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.hero__action--secondary'), this._typography.secondaryAction);
  }
}

if (!customElements.get('qds-hero')) {
  customElements.define('qds-hero', QdsHero);
}

export default QdsHero;
